//region :: Imports ::
import MComponent from '../Component';
import {createRef, ReactElement, RefObject} from 'react';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {_, MNullable} from '../../Utils';

//endregion

/** */
export type MFormOutcome = 'success' | 'errored' | 'cancelled';

/** */
type Props<TData extends object> = {
    initialValues: TData;
    validationSchema: any;
    formId: string;
    onBeforeSubmit?: (submittedValues: TData, formikHelpers: FormikHelpers<TData>) => boolean;
    onSubmit: (submittedValues: TData, formikHelpers: FormikHelpers<TData>) => Promise<void>;
    onError: (exception: any) => boolean;
    onAfterSubmit?: (outcome: MFormOutcome, submittedValues: TData, formikHelpers: FormikHelpers<TData>, exception: MNullable<any>) => void;
    onReset?: (fieldValues: TData, formikHelpers: FormikHelpers<TData>) => void;
    children: JSX.Element | JSX.Element[];
}

/** */
class MForm<TData extends object> extends MComponent<Props<TData>> {

    private m_submitting: boolean;
    private readonly m_formikRef: RefObject<FormikProps<TData>>;

    /** */
    public constructor(props: Props<TData>) {
        super(props);

        this.bindMethods();
        this.m_submitting = false;
        this.m_formikRef = createRef<FormikProps<TData>>();

    }

    /** */
    private bindMethods(): void {

        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.getFieldValue = this.getFieldValue.bind(this);

    }

    /** */
    private async form_onSubmit(submittedValues: TData, formikHelpers: FormikHelpers<TData>): Promise<void> {

        if(this.m_submitting) {
            return Promise.resolve();
        }

        let outcome: MFormOutcome = 'success';
        let cancel: boolean = false;
        let exception: MNullable<any> = null;

        this.m_submitting = true;

        try {

            if(this.props.onBeforeSubmit) {

                cancel = this.props.onBeforeSubmit(submittedValues, formikHelpers);
                outcome = cancel ? 'cancelled' : outcome;

            }

            if(!cancel) {
                await this.props.onSubmit(submittedValues, formikHelpers);
            }

        } catch(ex: any) {

            exception = ex;
            outcome = 'errored';

            const handled = this.props.onError === undefined
                            ? false : this.props.onError(ex);

            if(!handled) {
                throw ex;
            }

        } finally {

            this.m_submitting = false;
            this.props.onAfterSubmit?.(
                outcome, submittedValues, formikHelpers, exception);

        }

    }

    /** */
    public getFieldValue(fieldName: string): any | undefined {
        return _.get(this.m_formikRef.current?.values, fieldName);
    }

    /** */
    public setFieldValue(fieldName: string, value: any): void {
        _.setFieldValue(fieldName, value);
    }

    /** */
    public render(): ReactElement {

        return (
            <Formik initialValues={this.props.initialValues}
                    validationSchema={this.props.validationSchema}
                    enableReinitialize={true}
                    innerRef={this.m_formikRef}
                    onSubmit={this.form_onSubmit}
                    onReset={this.props.onReset}>

                <div>
                    <Form id={this.props.formId} method="POST">
                        {this.props.children}
                    </Form>
                </div>

            </Formik>
        );

    }

}

export default MForm;
